import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useSnackbar } from "src/components/snackbar";
import {
  Container,
  Card,
  Box,
  Typography,
  Stack,
  CircularProgress,
} from "@material-ui/core";
import Page from "src/components/Page";
import Label from "src/components/Label";
import { useGetEstadoPeriodoQuery } from "src/services/facturasApi";
import usePermisoFacturacion from "src/components/facturacion/usePermisoFacturacion";
import ResumenFacturacion from "src/components/facturacion/ResumenFacturacion";
import FiltrosFacturacion from "src/components/facturacion/FiltrosFacturacion";
import TablaFacturacion from "src/components/facturacion/TablaFacturacion";
import AccionesFacturacion from "src/components/facturacion/AccionesFacturacion";
import PanelOmitidos from "src/components/facturacion/PanelOmitidos";
import SelectorPeriodo from "src/components/facturacion/SelectorPeriodo";
import {
  FILTRO_DETENIDO,
  estaDetenido,
} from "src/components/facturacion/estadoFacturacion";

const FILTROS_INICIALES = { tipo: "TODOS", estado: "TODOS", texto: "" };

export default function FacturacionPlanes() {
  const { fecha } = useParams(); // YYYY-MM si es historial; undefined = periodo vigente
  const esHistorial = Boolean(fecha);

  const permiso = usePermisoFacturacion();
  const { enqueueSnackbar } = useSnackbar();

  // Patron real del proyecto: desestructurar sucursal del objeto auth.user
  const { sucursal: sucursalObtenida } = useSelector(
    (state) => state.auth.user,
  );
  const sucursal = sucursalObtenida?.refSucursal;

  const [filtros, setFiltros] = useState(FILTROS_INICIALES);

  // El intervalo de polling se ajusta en un efecto una vez que llega el estado
  // del periodo; no se puede leer data dentro de su propia declaracion (zona
  // muerta temporal), por eso se mantiene en estado separado.
  const [pollingInterval, setPollingInterval] = useState(0);
  // Recuerda si veniamos drenando, para notificar el cierre una sola vez al
  // pasar a finalizado (no en el primer render de un periodo ya terminado).
  const estabaDrenandoRef = useRef(false);

  const { data, isLoading, isFetching } = useGetEstadoPeriodoQuery(
    { sucursal, fecha },
    { skip: !sucursal, pollingInterval },
  );

  useEffect(() => {
    // Pollea mientras hay un drain en curso. Se usa la señal autoritativa del
    // backend: el lease se toma al iniciar facturar (antes del 202), asi que el
    // refetch posterior ya lo ve activo aunque ningun item este EMITIENDO todavia.
    // El estado FACTURANDO es respaldo. Nunca en historial; se apaga solo al
    // liberarse el lease (fin del drain).
    const drenando =
      !esHistorial &&
      (data?.estado === "FACTURANDO" ||
        (data?.lease?.activo && data?.lease?.heartbeatFresco));
    setPollingInterval(drenando ? 3000 : 0);

    // Notificacion de cierre: solo al pasar de drenando -> finalizado.
    if (estabaDrenandoRef.current && data?.finalizado) {
      const t = data.totales ?? {};
      const partes = [`${t.emitidas ?? 0} emitidas`];
      if (t.errores) partes.push(`${t.errores} con error`);
      // Los detenidos van contados dentro de los errores; se nombran aparte porque son los
      // unicos que no se resuelven reintentando.
      if (t.revision) partes.push(`${t.revision} de ellas detenidas`);
      if (t.omitidas) partes.push(`${t.omitidas} omitidas`);
      enqueueSnackbar(`Facturación finalizada: ${partes.join(", ")}.`, {
        variant: t.errores ? "warning" : "success",
      });
    }
    estabaDrenandoRef.current = drenando;
  }, [
    data?.estado,
    data?.lease?.activo,
    data?.lease?.heartbeatFresco,
    data?.finalizado,
    data?.totales,
    esHistorial,
    enqueueSnackbar,
  ]);

  const items = useMemo(() => data?.items ?? [], [data?.items]);

  const itemsFiltrados = useMemo(() => {
    const txt = filtros.texto.trim().toLowerCase();
    return items.filter((i) => {
      if (filtros.tipo !== "TODOS" && i.tipoFrecuencia !== filtros.tipo)
        return false;
      // "Detenido" no es un estado del ítem sino el subconjunto de los errores que quedó
      // esperando una reconciliación; el resto de las opciones sí compara contra el estado.
      if (filtros.estado === FILTRO_DETENIDO) {
        if (!estaDetenido(i)) return false;
      } else if (filtros.estado !== "TODOS" && i.estado !== filtros.estado)
        return false;
      if (
        txt &&
        !`${i.nombreCliente} ${i.nombrePlan}`.toLowerCase().includes(txt)
      )
        return false;
      return true;
    });
  }, [items, filtros]);

  const soloLectura = esHistorial;

  if (!permiso.puedeVer) {
    return (
      <Container>
        <Typography variant="h6">
          No tiene acceso a la facturación de planes.
        </Typography>
      </Container>
    );
  }

  return (
    <Page title="Facturación de planes">
      <Container maxWidth="xl">
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography variant="h4">Facturación de planes</Typography>
            <Typography variant="body2" color="text.secondary">
              {esHistorial ? "Periodo anterior" : "Periodo vigente"}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5} alignItems="center">
            {soloLectura && (
              <Label color="info" variant="ghost">
                Solo lectura
              </Label>
            )}
            <SelectorPeriodo sucursal={sucursal} fechaActual={fecha} />
          </Stack>
        </Stack>

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={2}>
            <ResumenFacturacion data={data} cargando={isFetching} />
            <AccionesFacturacion
              sucursal={sucursal}
              data={data}
              items={items}
              itemsFiltrados={itemsFiltrados}
              permiso={permiso}
              soloExportar={soloLectura}
            />
            <Card>
              <FiltrosFacturacion filtros={filtros} setFiltros={setFiltros} />
              <TablaFacturacion
                sucursal={sucursal}
                items={itemsFiltrados}
                data={data}
                soloLectura={soloLectura}
                permiso={permiso}
              />
            </Card>
            <PanelOmitidos
              sucursal={sucursal}
              items={items}
              permiso={permiso}
              soloLectura={soloLectura}
            />
          </Stack>
        )}
      </Container>
    </Page>
  );
}
